// services/tenantBillingApi.js
// The Super Admin's calls for tenant subscription and billing
// (doc/5.10.2026.md; Backend/api/routes/tenantBillingRoutes.js). Plain
// functions rather than a slice: the Payment tab and the Plan page each hold
// their own copy of one tenant's billing, and nothing else reads it.
// Imported under a non-`use` name: the default export is a plain axios
// factory, not a hook (see hooks/usePermissions.js).
import createAuthedAxios from '../hooks/useAxiosAuth';

const api = () => createAuthedAxios();
const base = (tenantId) => `/tenant-billing/${encodeURIComponent(tenantId)}`;

export const PAYMENT_METHODS = ['cash', 'upi', 'bank_transfer', 'cheque'];

/** The server's own words when it refused, else the fallback. */
export const billingErrorText = (err, fallback) => {
    const m = err?.response?.data?.message;
    if (typeof m === 'string' && m) return m;
    if (m && typeof m === 'object' && m.en) return m.en;
    return fallback;
};

export async function fetchTenantBilling(tenantId) {
    const res = await api().get(base(tenantId));
    return res.data?.data || null;
}

export async function saveTenantBillingSettings(tenantId, { noFee, planEndAction }) {
    const res = await api().put(`${base(tenantId)}/settings`, { noFee, planEndAction });
    return res.data?.data || null;
}

/** What a plan would be — `{ mode, period, discountType, discountValue, services?, features? }`. */
export async function quoteTenantPlan(tenantId, body) {
    const res = await api().post(`${base(tenantId)}/quote`, body);
    return res.data?.data || null;
}

export async function createTenantPlan(tenantId, body) {
    const res = await api().post(`${base(tenantId)}/plans`, body);
    return res.data || null;
}

export async function cancelTenantPlan(tenantId, tenantPlanId) {
    const res = await api().post(`${base(tenantId)}/plans/${encodeURIComponent(tenantPlanId)}/cancel`, {});
    return res.data || null;
}

export async function markTenantInvoicePaid(tenantId, invoiceNo, { method, reference, paidOn }) {
    const res = await api().post(`${base(tenantId)}/invoices/${encodeURIComponent(invoiceNo)}/mark-paid`, { method, reference, paidOn });
    return res.data || null;
}

export async function downloadTenantInvoice(tenantId, invoiceNo) {
    const res = await api().get(`${base(tenantId)}/invoices/${encodeURIComponent(invoiceNo)}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${invoiceNo}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}
